/**
 * תוכן צ׳קליסט — טיוטה 0.1 (מסלול התוכן, D11).
 *
 * מקור: `CHECKLIST-CONTENT-DRAFT.he.md` בתיקיית המסמכים, §4, כפי שהוא — בלי
 * תוספת סעיפים, מספרים או תנאי תחולה שאינם שם. זה **חומר ייבוא**: מנהל מייבא
 * אותו כטיוטת תבנית במסד, עורך ומפרסם. המנוע והמסכים קוראים את התוכן מהמסד,
 * לא מכאן (CQ-04).
 *
 * אזהרה (REGULATORY-MATRIX §0, GAP-05): הפרקים המקצועיים לא אומתו. ״נוסח אומת״
 * פירושו שנוסח הכלל שבעמודת המקור אומת — **לא** שהסעיף נבדק מקצועית. אף סעיף
 * כאן אינו ״התאמה מקצועית נבדקה״.
 */

import type { StageKind } from './siteProfile.ts';

export type AnswerScheme = 'plan_single' | 'standard';

export type ContentVerification = 'unverified' | 'text_verified' | 'professionally_reviewed';

/** העובדות שכלל תחולה רשאי להישען עליהן (`SiteFacts`). */
export const APPLICABILITY_FACTS = [
  'permitAreaAtLeastSiteManagerThreshold',
  'hasActiveSiteManager',
  'requiresSection192Notice',
  'safetyPlanKind',
  // תוכן 0.2 (06.10.2026): עובדה מהפרופיל ועובדה מהביקור, ״נדרש בירור״ כברירת מחדל.
  'employsForeignWorkers',
  'isFollowUpVisit',
] as const;

export type ApplicabilityFact = (typeof APPLICABILITY_FACTS)[number];

/**
 * תנאי תחולה. `fact` מתייחס לעובדות מפרופיל האתר ומהביקור (`siteFacts`);
 * `stage_kind_any` — כלל מוצר (CQ-07): הפרק חל אם אחד מסוגי השלבים נבחר בפתיחת הביקור;
 * `optional` — פרק ״לבחירה״ (D38): אינו בביקור עד שהבקר בוחר בו, בפתיחה או במהלך הביקור.
 */
export type ApplicabilityRule =
  | { kind: 'always' }
  | {
      kind: 'fact';
      fact: ApplicabilityFact;
      equals: string;
      /** תיאור התנאי בשפת אדם — מוצג כסיבת ההופעה. */
      label: string;
    }
  | {
      kind: 'stage_kind_any';
      kinds: StageKind[];
      /** שם השלב בשפת אדם (״חפירות ודיפון״) — לסיבת ההופעה/הסינון. */
      label: string;
    }
  | {
      kind: 'optional';
      /** ״פרק לבחירה״ — לתצוגה בלבד (D38). */
      label: string;
    };

export interface ContentQuestion {
  code: string;
  title: string;
  whatToCheck: string;
  sourceText: string;
  verification: ContentVerification;
  applicability: ApplicabilityRule;
}

export interface ContentChapter {
  code: string;
  title: string;
  scheme: AnswerScheme;
  questions: ContentQuestion[];
}

export interface ChecklistContent {
  title: string;
  versionLabel: string;
  isDemo: boolean;
  chapters: ContentChapter[];
}

const always: ApplicabilityRule = { kind: 'always' };
const u = (
  code: string,
  title: string,
  whatToCheck: string,
  sourceText: string,
): ContentQuestion => ({
  code,
  title,
  whatToCheck,
  sourceText,
  verification: 'unverified',
  applicability: always,
});
const v = (
  code: string,
  title: string,
  whatToCheck: string,
  sourceText: string,
): ContentQuestion => ({
  ...u(code, title, whatToCheck, sourceText),
  verification: 'text_verified',
});

export const CHECKLIST_DRAFT_0_1: ChecklistContent = {
  title: 'צ׳קליסט ביקור בטיחות — טיוטת תוכן',
  versionLabel: '0.1',
  isDemo: true,
  chapters: [
    {
      code: '1',
      title: 'תוכנית הבטיחות',
      scheme: 'plan_single',
      questions: [
        v(
          'C1-01',
          'קיום ויישום תוכנית הבטיחות במועד הביקור',
          'תוכנית קיימת באתר ומצורפת לפנקס הכללי · סוג התוכנית מתאים למספר העובדים (עד 49 / 50+) · הגרסה העדכנית · המענים לסיכונים שבתוכנית מיושמים בשטח',
          'R-02, R-05, R-10, R-17',
        ),
      ],
    },
    {
      code: '2',
      title: 'ניהול ותיעוד באתר',
      scheme: 'standard',
      questions: [
        u(
          'C2-01',
          'מנהל עבודה ממונה ונוכח',
          'מינוי, נוכחות באתר, שלט עם פרטיו',
          'תקנות עבודות בנייה',
        ),
        u('C2-02', 'פנקס כללי', 'קיים באתר, מעודכן', 'תקנות עבודות בנייה'),
        v('C2-03', 'ביקורת יומית של מנהל העבודה', 'מתועדת', 'R-16'),
        v(
          'C2-04',
          'מינויים נדרשים',
          'מנהל אתר (אם נדרש), ממונה בטיחות (אם נדרש), ללא ניגוד תפקידים',
          'R-03, R-12',
        ),
        u(
          'C2-05',
          'תסקירים ובדיקות ציוד',
          'תסקירים בתוקף לציוד הרמה, פיגומים ממוכנים וכו׳',
          'פקודת הבטיחות בעבודה',
        ),
      ],
    },
    {
      code: '3',
      title: 'ציוד מגן אישי',
      scheme: 'standard',
      questions: [
        u('C3-01', 'קסדות מגן', 'כל העובדים באתר חובשים', 'תקנות ציוד מגן אישי; R-05 (מיגון)'),
        u('C3-02', 'נעלי בטיחות', 'כל העובדים', 'תקנות ציוד מגן אישי'),
        u('C3-03', 'אפוד זוהר', 'באזורי תנועת כלים', 'תקנות ציוד מגן אישי'),
        u(
          'C3-04',
          'ציוד לעבודה בגובה',
          'רתמה, חיבור לנקודת עיגון, בשימוש בפועל',
          'תקנות עבודה בגובה',
        ),
        u(
          'C3-05',
          'מגן עיניים / שמיעה / כפפות',
          'לפי המשימה (חיתוך, ריתוך, רעש)',
          'תקנות ציוד מגן אישי',
        ),
      ],
    },
    {
      code: '4',
      title: 'גידור, שילוט ודרכי גישה',
      scheme: 'standard',
      questions: [
        u('C4-01', 'גידור היקפי', 'רציף, תקין, שער נסגר', 'תקנות עבודות בנייה'),
        u('C4-02', 'שילוט אזהרה', 'בכניסה ובאזורי סיכון', 'תקנות עבודות בנייה'),
        u('C4-03', 'הגנה על עוברי אורח', 'מעבר מוגן/מקורה ליד מבנה בבנייה', 'תקנות עבודות בנייה'),
        u('C4-04', 'דרכי גישה ומעברים', 'פנויים, מוארים, יציבים', 'תקנות עבודות בנייה'),
        u('C4-05', 'סדר וניקיון', 'פסולת, מסמרים, חומרים חוסמים', 'תקנות עבודות בנייה'),
      ],
    },
    {
      code: '5',
      title: 'עבודה בגובה, שפות ופתחים',
      scheme: 'standard',
      questions: [
        u(
          'C5-01',
          'מעקות בשפות תקרה',
          'מעקה שלם (אזן יד, אזן תיכון, לוח רגל)',
          'תקנות עבודות בנייה; תקנות עבודה בגובה',
        ),
        u(
          'C5-02',
          'פתחים ברצפה',
          'מכוסים בכיסוי יציב ומסומן, או מגודרים',
          'תקנות עבודות בנייה; תקנות עבודה בגובה',
        ),
        u(
          'C5-03',
          'גרמי מדרגות',
          'מעקה תקני (לא לוח עץ מאולתר)',
          'תקנות עבודות בנייה; תקנות עבודה בגובה',
        ),
        u('C5-04', 'פירי מעליות ופתחים אנכיים', 'מגודרים', 'תקנות עבודות בנייה; תקנות עבודה בגובה'),
        u('C5-05', 'הסמכת עובדים לעבודה בגובה', 'יש הסמכה בתוקף', 'תקנות עבודה בגובה'),
      ],
    },
    {
      code: '6',
      title: 'פיגומים',
      scheme: 'standard',
      questions: [
        u(
          'C6-01',
          'פיגום חיצוני / זקפים',
          'יציבות, עיגון למבנה, בסיס',
          'תקנות עבודות בנייה (פיגומים)',
        ),
        u('C6-02', 'משטחי עבודה', 'שלמים, ללא רווחים, מחוברים', 'תקנות עבודות בנייה (פיגומים)'),
        u('C6-03', 'מעקות ולוח רגל בפיגום', 'קיימים בכל משטח', 'תקנות עבודות בנייה (פיגומים)'),
        u(
          'C6-04',
          'פיגום נייד (אקרו וכו׳)',
          'נבנה לפי הוראות היצרן, גלגלים נעולים, פתחי עלייה',
          'תקנות עבודות בנייה (פיגומים)',
        ),
        u(
          'C6-05',
          'פיגום תלוי / ממוכן',
          'תסקיר בתוקף, הפעלה מוסמכת',
          'תקנות עבודות בנייה (פיגומים)',
        ),
        u('C6-06', 'גישה לפיגום', 'סולם/מדרגות תקינים', 'תקנות עבודות בנייה (פיגומים)'),
      ],
    },
    {
      code: '7',
      title: 'סולמות',
      scheme: 'standard',
      questions: [
        u('C7-01', 'תקינות הסולם', 'שלם, יציב, תקני', 'תקנות עבודות בנייה; תקנות עבודה בגובה'),
        u(
          'C7-02',
          'שימוש נכון',
          'מוצב נכון, מעוגן, בולט מעל נקודת היציאה',
          'תקנות עבודות בנייה; תקנות עבודה בגובה',
        ),
      ],
    },
    {
      code: '8',
      title: 'חפירות ודיפון',
      scheme: 'standard',
      questions: [
        u(
          'C8-01',
          'דיפון או שיפוע דפנות',
          'בחפירה עמוקה — דיפון או שיפוע יציב',
          'תקנות עבודות בנייה (חפירות)',
        ),
        u('C8-02', 'גידור שפת החפירה', 'מגודרת ומסומנת', 'תקנות עבודות בנייה (חפירות)'),
        u('C8-03', 'עפר וציוד ליד השפה', 'מורחקים מהשפה', 'תקנות עבודות בנייה (חפירות)'),
        u('C8-04', 'ירידה ועלייה', 'סולם / רמפה בטוחה', 'תקנות עבודות בנייה (חפירות)'),
      ],
    },
    {
      code: '9',
      title: 'חשמל',
      scheme: 'standard',
      questions: [
        u('C9-01', 'לוחות חשמל', 'סגורים, מסומנים, מוגנים ממים', 'תקנות החשמל'),
        u('C9-02', 'מפסק מגן (פחת) והארקה', 'קיים ותקין', 'תקנות החשמל'),
        u('C9-03', 'כבלים ומאריכים', 'ללא חשיפה, לא בשלוליות, לא על הרצפה במעברים', 'תקנות החשמל'),
        u('C9-04', 'קווי חשמל עיליים', 'מרחק בטוח מעבודה ומכלים', 'תקנות החשמל'),
      ],
    },
    {
      code: '10',
      title: 'הרמה ועגורנים',
      scheme: 'standard',
      questions: [
        u('C10-01', 'עגורן / מכונת הרמה', 'תסקיר בתוקף', 'פקודת הבטיחות בעבודה; תקנות עגורנים'),
        u('C10-02', 'עגורנאי ואתת', 'מוסמכים', 'פקודת הבטיחות בעבודה; תקנות עגורנים'),
        u('C10-03', 'אביזרי הרמה', 'תקינים, מסומנים, נבדקו', 'פקודת הבטיחות בעבודה; תקנות עגורנים'),
        u(
          'C10-04',
          'אזור ההרמה',
          'מגודר, אין עובדים מתחת למטען',
          'פקודת הבטיחות בעבודה; תקנות עגורנים',
        ),
      ],
    },
    {
      code: '11',
      title: 'טפסות ויציקות',
      scheme: 'standard',
      questions: [
        u('C11-01', 'תמיכות וטפסות', 'יציבות לפי תכנון', 'תקנות עבודות בנייה'),
        u('C11-02', 'משטחי עבודה ומעקות בטפסה', 'קיימים', 'תקנות עבודות בנייה'),
        u('C11-03', 'ברזלים בולטים', 'מכוסים', 'תקנות עבודות בנייה'),
      ],
    },
    {
      code: '12',
      title: 'חומרים מסוכנים ומניעת אש',
      scheme: 'standard',
      questions: [
        u(
          'C12-01',
          'אחסון חומרים דליקים',
          'במאצרה/אזור ייעודי, מסומן',
          'תקנות עבודות בנייה; דיני כבאות',
        ),
        u(
          'C12-02',
          'מטפי כיבוי',
          'קיימים בשטחי העבודה ובאזור החומרים הדליקים, בתוקף',
          'תקנות עבודות בנייה; דיני כבאות',
        ),
        u(
          'C12-03',
          'בלוני גז',
          'מאובטחים, מופרדים, עם ווסתים תקינים',
          'תקנות עבודות בנייה; דיני כבאות',
        ),
        u(
          'C12-04',
          'עבודות חמות (ריתוך/חיתוך)',
          'אמצעי כיבוי זמינים, הרחקת דליקים',
          'תקנות עבודות בנייה; דיני כבאות',
        ),
      ],
    },
    {
      code: '13',
      title: 'ציוד מכני וכלי עבודה',
      scheme: 'standard',
      questions: [
        u('C13-01', 'כלים חשמליים ידניים', 'תקינים, עם מגינים', 'פקודת הבטיחות בעבודה'),
        u(
          'C13-02',
          'ציוד מכני הנדסי',
          'מפעיל מוסמך, צופר נסיעה לאחור, הפרדה מהולכי רגל',
          'פקודת הבטיחות בעבודה',
        ),
      ],
    },
    {
      code: '14',
      title: 'רווחה, עזרה ראשונה והדרכות',
      scheme: 'standard',
      questions: [
        u('C14-01', 'שירותים ומי שתייה', 'קיימים ונגישים', 'תקנות עבודות בנייה'),
        u('C14-02', 'עזרה ראשונה', 'ארגז/ציוד זמין, מלא', 'תקנות עזרה ראשונה'),
        v(
          'C14-03',
          'הדרכות לעובדים',
          'תיעוד הדרכה לסיכונים הקשורים לעבודה (פנים אל פנים)',
          'R-05, T-03',
        ),
        v('C14-04', 'עובדים זרים', 'הדרכה נוספת בתוקף', 'T-06'),
      ],
    },
  ],
};
